import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { statusLabel, t } from '../i18n';
import {
  ASSOCIATION_STATUSES,
  CAPITAL_LEGAL_FORMS,
  CUSTOMER_TYPE_OPTIONS,
  normalizeCustomerType,
} from '../utils/verificationCompanyModel';

const API_URL = process.env.REACT_APP_API_URL || '/api';
function normalizeAssociationStatus(value) {
  const normalized = String(value || '').trim();
  return normalized === 'Verein' ? 'Nicht eingetragener Verein' : normalized;
}

function VerifyForm() {
  const { token } = useParams();
  const messageRef = useRef(null);

  const [verification, setVerification] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadingError, setLoadingError] = useState('');
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    birth_date: '',
    street: '',
    postal_code: '',
    city: '',
    country: '',
    customer_type: 'privat',
    company_name: '',
    legal_form: '',
    representative_name: '',
    business_activity: '',
    company_street: '',
    company_postal_code: '',
    company_city: '',
    company_country: '',
    register_court: '',
    register_number: '',
    tax_number: '',
    vat_id: '',
    planned_foundation_date: '',
  });

  const [files, setFiles] = useState({
    id_front: null,
    id_back: null,
    selfie: null,
    business_doc: null,
    extra_doc: null,
  });
  const [deleteDocuments, setDeleteDocuments] = useState([]);
  const [submittedSummary, setSubmittedSummary] = useState(null);

  const [submitting, setSubmitting] = useState(false);
  const [submitSucceeded, setSubmitSucceeded] = useState(false);
  const [message, setMessage] = useState('');

  useEffect(() => {
    const fetchVerification = async () => {
      setLoading(true);
      setLoadingError('');

      try {
        const response = await fetch(`${API_URL}/verify/${token}`);
        const data = await response.json();

        if (!response.ok) {
          throw new Error(data.error || t('error_load_verification'));
        }

        setVerification(data);

        const usageAgreementData = data.usage_agreement_data || {};
        const nameParts = (data.customer.name || '').trim().split(' ');
        const firstName = nameParts.length > 0 ? nameParts[0] : '';
        const lastName = nameParts.length > 1 ? nameParts.slice(1).join(' ') : '';

        setFormData((prev) => ({
          ...prev,
          first_name: usageAgreementData.first_name || firstName,
          last_name: usageAgreementData.last_name || lastName,
          email: usageAgreementData.email || data.customer.email || '',
          phone: usageAgreementData.phone || '',
          birth_date: usageAgreementData.birth_date || '',
          street: usageAgreementData.street || '',
          postal_code: usageAgreementData.postal_code || '',
          city: usageAgreementData.city || '',
          country: usageAgreementData.country || data.customer.country || '',
          customer_type: normalizeCustomerType(data.customer.customer_type || usageAgreementData.agreement_type || 'privat'),
          company_name: usageAgreementData.company_name || data.customer.company_name || '',
          legal_form: normalizeCustomerType(data.customer.customer_type || usageAgreementData.agreement_type) === 'verein'
            ? normalizeAssociationStatus(usageAgreementData.legal_form)
            : usageAgreementData.legal_form || '',
          representative_name: usageAgreementData.representative_name || data.customer.name || '',
          business_activity: usageAgreementData.business_activity || '',
          company_street: usageAgreementData.company_street || '',
          company_postal_code: usageAgreementData.company_postal_code || '',
          company_city: usageAgreementData.company_city || '',
          company_country: usageAgreementData.company_country || '',
          register_court: usageAgreementData.register_court || '',
          register_number: usageAgreementData.register_number || '',
          tax_number: usageAgreementData.tax_number || '',
          vat_id: usageAgreementData.vat_id || '',
          planned_foundation_date: usageAgreementData.planned_foundation_date || '',
        }));
      } catch (err) {
        console.error(err);
        setLoadingError(err.message || t('error_loading'));
      } finally {
        setLoading(false);
      }
    };

    fetchVerification();
  }, [token]);

  useEffect(() => {
    if (message && messageRef.current) {
      messageRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, [message]);

  const normalizedCustomerType = useMemo(() => {
    if (formData.customer_type === 'gründung') {
      return 'gruendung';
    }

    if (['freiberufler', 'freiberuflich', 'freiberuflerin'].includes(formData.customer_type)) {
      return 'freelancer';
    }

    return formData.customer_type;
  }, [formData.customer_type]);

  const isFreelancer = normalizedCustomerType === 'freelancer';
  const isSoleProprietor = normalizedCustomerType === 'einzelunternehmen';
  const isGermanCorporation = normalizedCustomerType === 'kapitalgesellschaft';
  const isAssociation = normalizedCustomerType === 'verein';
  const isRegisteredAssociation = isAssociation && formData.legal_form === 'e. V.';
  const isAssociationRegistrationPending = isAssociation && formData.legal_form === 'Verein gegründet – Eintragung beantragt';
  const isUnregisteredAssociation = isAssociation && formData.legal_form === 'Nicht eingetragener Verein';
  const isAssociationFormationPlanned = isAssociation && formData.legal_form === 'Vereinsgründung geplant';
  const isForeignBusiness = normalizedCustomerType === 'ausland';
  const isPlannedFounding = normalizedCustomerType === 'gruendung';
  const needsBusinessDoc = isSoleProprietor || isGermanCorporation || isAssociation || isForeignBusiness;
  const showPersonalDetails = true;
  const showCompanyName = isFreelancer || isSoleProprietor || isGermanCorporation || isAssociation || isForeignBusiness || isPlannedFounding;
  const showCapitalLegalForm = isGermanCorporation;
  const showAssociationLegalForm = isAssociation;
  const showForeignLegalForm = isForeignBusiness;
  const showRepresentative = isGermanCorporation || isAssociation || isForeignBusiness || isPlannedFounding;
  const showBusinessActivity = isFreelancer || isSoleProprietor || isGermanCorporation || isAssociation || isForeignBusiness || isPlannedFounding;
  const showRegisterFields = isGermanCorporation || isRegisteredAssociation || isAssociationRegistrationPending || isForeignBusiness;
  const showAssociationAddress = isAssociation && !isAssociationFormationPlanned;
  const showAssociationFoundationDate = isAssociation && !isRegisteredAssociation;
  const associationNeedsFoundingProtocol = isAssociationRegistrationPending || isUnregisteredAssociation;
  const companyNameRequired = isGermanCorporation || isAssociation || isForeignBusiness;
  const representativeRequired = isGermanCorporation || isAssociation || isForeignBusiness;
  const companyNameLabel = isFreelancer
    ? t('business_designation_optional')
    : isSoleProprietor
      ? t('trade_name_optional')
      : isPlannedFounding
        ? t('planned_company_name_optional')
        : isAssociation
          ? t('association_name')
        : isForeignBusiness
          ? t('foreign_company_name')
          : t('company_name');
  const businessActivityLabel = isAssociation
    ? t('association_purpose')
    : isFreelancer
    ? t('activity_optional')
    : t('business_activity_optional');
  const representativeLabel = representativeRequired
    ? (isAssociation ? t('association_representative') : t('representative'))
    : t('representative_optional');

  const businessDocumentLabel = isSoleProprietor
    ? t('trade_registration')
    : isGermanCorporation
      ? t('commercial_register_extract_or_proof')
      : isAssociation
        ? isRegisteredAssociation
          ? t('association_register_extract')
          : isAssociationFormationPlanned
            ? t('association_draft_statutes')
            : t('association_statutes')
      : isForeignBusiness
        ? t('foreign_business_proof')
        : t('business_doc_full');

  const alreadySubmitted = verification?.already_submitted || false;
  const shouldUsePortal = verification?.should_use_portal || false;
  const existingDocumentTypes = useMemo(
    () => new Set(verification?.existing_document_types || []),
    [verification?.existing_document_types]
  );
  const existingDocumentsByType = useMemo(
    () => (verification?.existing_documents || []).reduce((acc, document) => {
      acc[document.document_type] = document;
      return acc;
    }, {}),
    [verification?.existing_documents]
  );
  const isDocumentMarkedForDelete = (documentType) => {
    if (documentType === 'business_doc') {
      return deleteDocuments.includes('business_doc') ||
        deleteDocuments.includes('gewerbeanmeldung') ||
        deleteDocuments.includes('handelsregisterauszug');
    }

    return deleteDocuments.includes(documentType);
  };
  const hasExistingDocument = (documentType) => {
    if (documentType === 'business_doc') {
      return (
        (
          existingDocumentTypes.has('business_doc') ||
          existingDocumentTypes.has('gewerbeanmeldung') ||
          existingDocumentTypes.has('handelsregisterauszug')
        ) &&
        !isDocumentMarkedForDelete('business_doc')
      );
    }

    return existingDocumentTypes.has(documentType) && !isDocumentMarkedForDelete(documentType);
  };

  const getExistingDocument = (documentType) => {
    if (documentType === 'business_doc') {
      return existingDocumentsByType.business_doc ||
        existingDocumentsByType.gewerbeanmeldung ||
        existingDocumentsByType.handelsregisterauszug ||
        null;
    }

    return existingDocumentsByType[documentType] || null;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleFileChange = (e) => {
    const { name, files: selectedFiles } = e.target;
    setFiles((prev) => ({
      ...prev,
      [name]: selectedFiles[0] || null,
    }));

    if (selectedFiles[0]) {
      setDeleteDocuments((prev) => prev.filter((item) => {
        if (name === 'business_doc') {
          return item !== 'business_doc' &&
            item !== 'gewerbeanmeldung' &&
            item !== 'handelsregisterauszug';
        }

        return item !== name;
      }));
    }
  };

  const toggleDeleteDocument = (documentType) => {
    setDeleteDocuments((prev) => {
      const aliases = documentType === 'business_doc'
        ? ['business_doc', 'gewerbeanmeldung', 'handelsregisterauszug']
        : [documentType];
      const hasAlias = aliases.some((alias) => prev.includes(alias));

      if (hasAlias) {
        return prev.filter((item) => !aliases.includes(item));
      }

      return [...prev, documentType];
    });
  };

  const moveToPortalState = (nextBusinessStatus) => {
    setVerification((prev) => ({
      ...prev,
      already_submitted: true,
      should_use_portal: true,
      identity_status: 'in_review',
      business_status: nextBusinessStatus,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (submitting || submitSucceeded) {
      return;
    }

    setSubmitting(true);
    setMessage('');

    try {
      const payload = new FormData();
      payload.append('first_name', formData.first_name);
      payload.append('last_name', formData.last_name);
      payload.append('email', formData.email);
      payload.append('phone', formData.phone);
      payload.append('birth_date', formData.birth_date);
      payload.append('street', formData.street);
      payload.append('postal_code', formData.postal_code);
      payload.append('city', formData.city);
      payload.append('country', formData.country);
      payload.append('customer_type', normalizedCustomerType);
      payload.append('agreement_type', normalizedCustomerType);
      payload.append('company_name', showCompanyName ? formData.company_name : '');
      payload.append('legal_form', (showCapitalLegalForm || showAssociationLegalForm || showForeignLegalForm || isPlannedFounding) ? formData.legal_form : '');
      payload.append('representative_name', showRepresentative ? formData.representative_name : '');
      payload.append('business_activity', showBusinessActivity ? formData.business_activity : '');
      payload.append('company_street', showAssociationAddress ? formData.company_street : '');
      payload.append('company_postal_code', showAssociationAddress ? formData.company_postal_code : '');
      payload.append('company_city', showAssociationAddress ? formData.company_city : '');
      payload.append('company_country', showAssociationAddress || isForeignBusiness ? formData.company_country : '');
      payload.append('register_court', showRegisterFields ? formData.register_court : '');
      payload.append('register_number', showRegisterFields ? formData.register_number : '');
      payload.append('tax_number', formData.tax_number);
      payload.append('vat_id', formData.vat_id);
      payload.append('planned_foundation_date', formData.planned_foundation_date);
      payload.append('delete_documents', JSON.stringify(deleteDocuments));

      if (files.id_front) payload.append('id_front', files.id_front);
      if (files.id_back) payload.append('id_back', files.id_back);
      if (files.selfie) payload.append('selfie', files.selfie);
      if (files.business_doc) payload.append('business_doc', files.business_doc);
      if (files.extra_doc) payload.append('extra_doc', files.extra_doc);

      const response = await fetch(`${API_URL}/verify/${token}`, {
        method: 'POST',
        body: payload,
      });

      const responseText = await response.text();
      let data = {};
      try {
        data = responseText ? JSON.parse(responseText) : {};
      } catch (parseError) {
        data = {};
      }

      if (response.ok) {
        const successMessage = isPlannedFounding
          ? t('submit_success_founding')
          : t('submit_success');

        setSubmittedSummary({
          customerType: t(CUSTOMER_TYPE_OPTIONS.find((option) => option.value === normalizedCustomerType)?.labelKey || 'customer_type'),
          organizationName: showCompanyName ? formData.company_name : '',
          legalForm: formData.legal_form,
          representative: showRepresentative ? formData.representative_name : '',
          businessDocument: files.business_doc?.name || getExistingDocument('business_doc')?.original_filename || '',
          extraDocument: files.extra_doc?.name || getExistingDocument('extra_doc')?.original_filename || '',
        });
        setMessage(successMessage);
        setSubmitSucceeded(true);
        moveToPortalState(
          normalizedCustomerType === 'privat'
            ? 'not_required'
            : normalizedCustomerType === 'freelancer'
              ? 'not_required'
            : normalizedCustomerType === 'gruendung'
              ? 'pending'
              : 'in_review'
        );
      } else if (response.status === 409) {
        setMessage(
          data.error ||
            t('already_submitted_message')
        );
        moveToPortalState(verification.business_status || 'pending');
      } else if (response.status === 413) {
        setMessage('Die hochgeladenen Dateien sind zu groß. Bitte laden Sie kleinere Dateien hoch oder senden Sie die Unterlagen per E-Mail.');
      } else {
        setMessage(data.error || t('error_generic'));
      }
    } catch (err) {
      console.error(err);
      setMessage(t('error_generic'));
    } finally {
      setSubmitting(false);
    }
  };

  const renderExistingDocumentControls = (documentType) => {
    const existingDocument = getExistingDocument(documentType);
    if (!existingDocument) return null;

    const markedForDelete = isDocumentMarkedForDelete(documentType);
    const replacementFile = files[documentType] || null;

    if (replacementFile) {
      return (
        <div className="document-state-box document-state-box--replace" role="status">
          <strong>{t('document_will_be_replaced')}</strong>
          <p>{existingDocument.original_filename || t('document_saved')}</p>
          <p><strong>{t('new_document')}:</strong> {replacementFile.name}</p>
          <small>{t('document_replace_explanation')}</small>
        </div>
      );
    }

    return (
      <div className={`document-state-box${markedForDelete ? ' document-state-box--delete' : ''}`} role="status">
        <strong>
          {markedForDelete ? t('document_marked_for_removal') : t('document_kept')}
        </strong>
        <p>
          {existingDocument.original_filename || t('document_saved')}
          {existingDocument.uploaded_at
            ? ` · ${new Date(existingDocument.uploaded_at).toLocaleDateString('de-DE')}`
            : ''}
        </p>
        <small>{markedForDelete ? t('document_removal_explanation') : t('replace_document_hint')}</small>
        <button
          type="button"
          className={markedForDelete ? 'secondary-button document-state-action' : 'danger-outline-button document-state-action'}
          onClick={() => toggleDeleteDocument(documentType)}
        >
          {markedForDelete ? t('undo_document_removal') : t('remove_existing_document')}
        </button>
      </div>
    );
  };

  if (loading) {
    return (
      <main className="page-shell">
        <div className="card">
          <p>{t('loading')}</p>
        </div>
      </main>
    );
  }

  if (loadingError) {
    return (
      <main className="page-shell">
        <div className="card">
          <p>{loadingError}</p>
        </div>
      </main>
    );
  }

  return (
    <main className="page-shell">
      <div className="brand-header">
        <div>
          <p className="eyebrow">YourGermanAddress</p>
          <h1>{t('portal_title')}</h1>
          <p className="subtitle">
            {t('verify_subtitle')}
          </p>
        </div>
      </div>

      <div className="card">
        <div className="status-grid">
          <div className="status-box">
            <span>{t('identity_status')}</span>
            <strong>{statusLabel(verification.identity_status)}</strong>
          </div>
          <div className="status-box">
            <span>{t('business_status')}</span>
            <strong>{statusLabel(verification.business_status)}</strong>
          </div>
        </div>
        {!alreadySubmitted ? <p className="status-context-note">{t('status_before_submission_note')}</p> : null}

        {message && (
          <div ref={messageRef} className="info-box">
            {message}
            {submitSucceeded ? (
              <div className="submission-confirmation">
                {submittedSummary ? <div className="submission-summary" role="status">
                  <strong>{t('submitted_summary_title')}</strong>
                  <dl>
                    <div><dt>{t('submitted_as')}</dt><dd>{submittedSummary.customerType}</dd></div>
                    {submittedSummary.organizationName ? <div><dt>{t('submitted_organization')}</dt><dd>{submittedSummary.organizationName}</dd></div> : null}
                    {submittedSummary.legalForm ? <div><dt>{t('submitted_legal_form')}</dt><dd>{submittedSummary.legalForm}</dd></div> : null}
                    {submittedSummary.representative ? <div><dt>{t('submitted_representative')}</dt><dd>{submittedSummary.representative}</dd></div> : null}
                    {submittedSummary.businessDocument ? <div><dt>{t('submitted_primary_document')}</dt><dd>{submittedSummary.businessDocument}</dd></div> : null}
                    {submittedSummary.extraDocument ? <div><dt>{t('submitted_additional_document')}</dt><dd>{submittedSummary.extraDocument}</dd></div> : null}
                  </dl>
                  <p>{t('submitted_summary_hint')}</p>
                </div> : null}
                <Link className="primary-link-button" to="/account/dashboard?tab=display">
                  {t('edit_mailbox_name')}
                </Link>
                <p style={{ marginTop: '8px', marginBottom: 0 }}>
                  {t('edit_mailbox_name_hint')}
                </p>
              </div>
            ) : null}
          </div>
        )}

        {shouldUsePortal ? (
          <div className="info-box">
            <strong>
              {alreadySubmitted
                ? t('already_submitted_title')
                : t('use_upload_link_title')}
            </strong>

            <p>
              {t('use_upload_link_text')}
            </p>

            {(verification.followup_request?.requested_documents || []).length > 0 && (
              <>
                <p>{t('currently_requested')}</p>
                <ul>
                  {(verification.followup_request?.requested_documents || []).map((doc) => (
                    <li key={doc}>{doc}</li>
                  ))}
                </ul>
              </>
            )}

        {verification.followup_request?.requested_message ? (
              <p>
                <strong>{t('note')}:</strong>
                <br />
                {verification.followup_request?.requested_message}
              </p>
            ) : null}

            <div style={{ marginTop: '14px' }}>
              <Link className="primary-link-button" to={`/portal/${token}`}>
                {t('upload_more')}
              </Link>
            </div>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="form-grid">
            <fieldset
              disabled={submitting || submitSucceeded}
              style={{ border: '0', padding: 0, margin: 0, minWidth: 0, display: 'contents' }}
            >
              <label>
                {t('first_name')}
                <input
                  type="text"
                  name="first_name"
                  value={formData.first_name}
                  onChange={handleChange}
                  required
                />
              </label>

              <label>
                {t('last_name')}
                <input
                  type="text"
                  name="last_name"
                  value={formData.last_name}
                  onChange={handleChange}
                  required
                />
              </label>

              <label>
                {t('email')}
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                />
              </label>

              <label>
                {t('phone_optional')}
                <input
                  type="text"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                />
              </label>

              {showPersonalDetails && (
                <>
                  <label>
                    {t('birth_date')}
                    <input
                      type="date"
                      name="birth_date"
                      value={formData.birth_date}
                      onChange={handleChange}
                      required
                    />
                  </label>

                  <label>
                    {t('street')}
                    <input
                      type="text"
                      name="street"
                      value={formData.street}
                      onChange={handleChange}
                      required
                    />
                  </label>

                  <label>
                    {t('postal_code')}
                    <input
                      type="text"
                      name="postal_code"
                      value={formData.postal_code}
                      onChange={handleChange}
                      required
                    />
                  </label>

                  <label>
                    {t('city')}
                    <input
                      type="text"
                      name="city"
                      value={formData.city}
                      onChange={handleChange}
                      required
                    />
                  </label>
                </>
              )}

              <label>
                {t('country')}
                <input
                  type="text"
                  name="country"
                  value={formData.country}
                  onChange={handleChange}
                  required
                />
              </label>


              <label>
                {t('customer_type')}
                <select
                  name="customer_type"
                  value={formData.customer_type}
                  onChange={handleChange}
                >
                  {CUSTOMER_TYPE_OPTIONS.map((option) => (
                    <option key={option.value} value={option.value}>
                      {t(option.labelKey)}
                    </option>
                  ))}
                </select>
              </label>

              {showCompanyName && (
                <>
                  <label className="full-width">
                    {companyNameLabel}
                    <input
                      type="text"
                      name="company_name"
                      value={formData.company_name}
                      onChange={handleChange}
                      required={companyNameRequired}
                    />
                  </label>

                </>
              )}

              {showCapitalLegalForm && (
                <label>
                  {t('legal_form')}
                  <select
                    name="legal_form"
                    value={formData.legal_form}
                    onChange={handleChange}
                    required
                  >
                    <option value="">{t('please_select')}</option>
                    {CAPITAL_LEGAL_FORMS.map((legalForm) => (
                      <option key={legalForm} value={legalForm}>
                        {legalForm}
                      </option>
                    ))}
                  </select>
                </label>
              )}

              {showAssociationLegalForm && (
                <label>
                  {t('legal_form')}
                  <select
                    name="legal_form"
                    value={formData.legal_form}
                    onChange={handleChange}
                    required
                  >
                    <option value="">{t('please_select')}</option>
                    {ASSOCIATION_STATUSES.map((status) => (
                      <option key={status.value} value={status.value}>{t(status.labelKey)}</option>
                    ))}
                  </select>
                </label>
              )}

              {showForeignLegalForm && (
                <>
                  <label>
                    {t('legal_form_foreign_optional')}
                    <input
                      type="text"
                      name="legal_form"
                      value={formData.legal_form}
                      onChange={handleChange}
                      placeholder={t('foreign_legal_form_placeholder')}
                    />
                  </label>

                  <label>
                    {t('company_country')}
                    <input
                      type="text"
                      name="company_country"
                      value={formData.company_country}
                      onChange={handleChange}
                      required
                    />
                  </label>
                </>
              )}

              {showRepresentative && (
                <label>
                  {representativeLabel}
                  <input
                    type="text"
                    name="representative_name"
                    value={formData.representative_name}
                    onChange={handleChange}
                    required={representativeRequired}
                  />
                </label>
              )}

              {showBusinessActivity && (
                <label className="full-width">
                  {businessActivityLabel}
                  <input
                    type="text"
                    name="business_activity"
                    value={formData.business_activity}
                    onChange={handleChange}
                    required={isAssociation}
                  />
                </label>
              )}

              {showAssociationAddress && (
                <>
                  <label className="full-width">
                    {t('association_current_street')}
                    <input type="text" name="company_street" value={formData.company_street} onChange={handleChange} required />
                  </label>
                  <label>
                    {t('association_current_postal_code')}
                    <input type="text" name="company_postal_code" value={formData.company_postal_code} onChange={handleChange} required />
                  </label>
                  <label>
                    {t('association_current_city')}
                    <input type="text" name="company_city" value={formData.company_city} onChange={handleChange} required />
                  </label>
                  <label className="full-width">
                    {t('association_current_country')}
                    <input type="text" name="company_country" value={formData.company_country} onChange={handleChange} required />
                  </label>
                </>
              )}

              {showRegisterFields && (
                <>
                  <label>
                    {isRegisteredAssociation ? t('association_register_court') : t('association_register_court_optional')}
                    <input
                      type="text"
                      name="register_court"
                      value={formData.register_court}
                      onChange={handleChange}
                      required={isRegisteredAssociation}
                    />
                  </label>

                  <label>
                    {isRegisteredAssociation ? t('association_register_number') : t('association_file_reference_optional')}
                    <input
                      type="text"
                      name="register_number"
                      value={formData.register_number}
                      onChange={handleChange}
                      required={isRegisteredAssociation}
                    />
                  </label>
                </>
              )}

              {isPlannedFounding && (
                <label className="full-width">
                  {t('planned_foundation_date_optional')}
                  <input
                    type="date"
                    name="planned_foundation_date"
                    value={formData.planned_foundation_date}
                    onChange={handleChange}
                  />
                </label>
              )}

              {showAssociationFoundationDate && (
                <label className="full-width">
                  {isAssociationFormationPlanned ? t('association_planned_foundation_date') : t('association_foundation_date')}
                  <input
                    type="date"
                    name="planned_foundation_date"
                    value={formData.planned_foundation_date}
                    onChange={handleChange}
                    required
                  />
                </label>
              )}

              <label>
                {t('id_front')}
                {renderExistingDocumentControls('id_front')}
                <input
                  type="file"
                  name="id_front"
                  accept="image/*,.pdf"
                  onChange={handleFileChange}
                  required={!hasExistingDocument('id_front')}
                />
              </label>

              <label>
                {t('id_back')}
                {renderExistingDocumentControls('id_back')}
                <input
                  type="file"
                  name="id_back"
                  accept="image/*,.pdf"
                  onChange={handleFileChange}
                  required={!hasExistingDocument('id_back')}
                />
              </label>

              <label className="full-width">
                {t('selfie')}
                {renderExistingDocumentControls('selfie')}
                <input
                  type="file"
                  name="selfie"
                  accept="image/*"
                  onChange={handleFileChange}
                  required={!hasExistingDocument('selfie')}
                />
              </label>

              {needsBusinessDoc && (
                <label className="full-width">
                  {businessDocumentLabel}
                  {renderExistingDocumentControls('business_doc')}
                  <input
                    type="file"
                    name="business_doc"
                    accept="image/*,.pdf"
                    onChange={handleFileChange}
                    required={!hasExistingDocument('business_doc')}
                  />
                </label>
              )}

              {associationNeedsFoundingProtocol && (
                <label className="full-width">
                  {t('association_founding_and_board_protocol')}
                  {renderExistingDocumentControls('extra_doc')}
                  <input
                    type="file"
                    name="extra_doc"
                    accept="image/*,.pdf"
                    onChange={handleFileChange}
                    required={!hasExistingDocument('extra_doc')}
                  />
                </label>
              )}

              {isFreelancer && (
                <div className="hint-box full-width">
                  <strong>{t('freelancer')}</strong>
                  <p>
                    {t('freelancer_hint_text')}
                  </p>
                </div>
              )}

              {isPlannedFounding && (
                <div className="hint-box full-width">
                  <strong>{t('planned_founding')}</strong>
                  <p>
                    {t('planned_founding_hint_text')}
                  </p>
                </div>
              )}

              {!associationNeedsFoundingProtocol && (
              <label className="full-width">
                {isRegisteredAssociation ? t('association_statutes_optional') : t('extra_doc_optional')}
                {renderExistingDocumentControls('extra_doc')}
                <input
                  type="file"
                  name="extra_doc"
                  accept="image/*,.pdf"
                  onChange={handleFileChange}
                />
              </label>
              )}

              <div className="full-width">
                <button
                  type="submit"
                  disabled={submitting || submitSucceeded}
                  className="primary-button"
                >
                  {submitSucceeded
                    ?
                     t('submitted_successfully')
                    : submitting
                      ?
                        t('submitting')
                      : t('submit_documents')}
                </button>
              </div>
            </fieldset>
          </form>
        )}
      </div>
    </main>
  );
}

export default VerifyForm;
